import Cropper from 'react-easy-crop';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { styled, css } from '@mui/system';
import { Modal as BaseModal } from '@mui/base/Modal';
import Fade from '@mui/material/Fade';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Slider } from '@mui/material';
import { getCroppedImg } from './cropperUilt';
import { useTranslation } from 'react-i18next';
import useDarkMode from '../../../hooks/useDarkMode';

export const CommonCropperModal = ({
	img,
	imgFile,
	isOpen,
	setOpen,
	onCloseModal,
	onComplete,
	aspectRatio,
	modalWidth,
	modalHeight,
	imageCropSize,
	cropShape,
	objectFit,
	setImg,
}) => {
	const { t } = useTranslation();
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [rotation, setRotation] = useState(0);
	const [zoom, setZoom] = useState(1);
	const [minZoom, setMinZoom] = useState(1);
	const [maxZoom, setMaxZoom] = useState(3);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const containerRef = useRef(null);

	const matches = useMediaQuery('(min-width:600px)');
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	const onCropComplete = (croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	};

	const showCroppedImage = async () => {
		try {
			const blobFile = await getCroppedImg(img, croppedAreaPixels, rotation, imgFile.type);
			const croppedImage = URL.createObjectURL(blobFile);
			//

			setImg(croppedImage);
			let file = new File([blobFile], imgFile.name, {
				type: imgFile.type ? imgFile.type : 'image/jpeg',
			});
			if (onComplete) onComplete(croppedImage, file);
		} catch (e) {
			console.error(e);
		}

		setOpen(false);
		resetValues();
	};

	const resetValues = () => {
		setZoom(1);
		setRotation(0);
		setCrop({ x: 0, y: 0 });
	};

	const onClose = () => {
		if (onCloseModal) onCloseModal();
		setOpen(false);
		resetValues();
	};

	return (
		<Modal
			aria-labelledby='transition-modal-title'
			aria-describedby='transition-modal-description'
			open={isOpen}
			onClose={(_, reason) => reason === 'backdropClick' && onClose}
			closeAfterTransition
			slots={{ backdrop: StyledBackdrop }}>
			<Fade in={isOpen}>
				<ModalContent
					sx={{
						height: modalHeight ?? '450px',
						width: modalWidth ?? matches ? '550px' : '330px',
						flexDirection: 'column',
						backgroundColor: darkModeStatus ? '#242632' : '#ececec',
						color: darkModeStatus ? 'white' : null,
					}}>
					<div
						className='pb-2 modal-header'
						style={{ paddingRight: '18px', paddingTop: '15px' }}>
						<h4 style={{ color: darkModeStatus ? 'white' : '#3d4349', margin: 0 }}>
							{t('Crop Image')}
						</h4>
					</div>
					<div
						style={{
							height: '100%',
							width: '100%',
							padding: '0px 10px 10px 10px',
							overflow: 'hidden',
						}}>
						<div
							ref={containerRef}
							style={{
								position: 'relative',
								width: '100%',
								height: '100%',
								background: 'black',
								overflow: 'hidden',
							}}>
							<Cropper
								style={{ cropAreaStyle: { color: '#000000b0' } }}
								image={img}
								crop={crop}
								rotation={rotation}
								cropSize={imageCropSize ?? undefined}
								zoom={zoom}
								minZoom={minZoom}
								maxZoom={maxZoom}
								cropShape={cropShape}
								objectFit={objectFit ?? 'contain'}
								aspect={aspectRatio ?? 1 / 1}
								onCropChange={setCrop}
								onRotationChange={setRotation}
								onCropComplete={onCropComplete}
								onZoomChange={setZoom}
							/>
						</div>
					</div>
					<div className='px-5'>
						<div>
							<label>{t('Zoom')}</label>
							<Slider
								value={zoom}
								min={minZoom}
								max={maxZoom}
								step={(maxZoom - minZoom) / 20}
								aria-labelledby='Zoom'
								onChange={(e, val) => {
									let z = val;
									if (z < minZoom) z = minZoom;
									if (z > maxZoom) z = maxZoom;
									setZoom(z);
								}}
							/>
						</div>
						<div>
							<label>{t('Rotation')}</label>
							<Slider
								value={rotation}
								min={0}
								max={360}
								step={1}
								aria-labelledby='Rotation'
								onChange={(e, rotation) => setRotation(rotation)}
							/>
						</div>
					</div>

					<div
						className='row'
						style={{
							height: '20%',
							alignContent: 'center',
						}}>
						<div className='col text-center h-100 d-flex align-items-center justify-content-center p-0'>
							<button
								type='button'
								className='btn btn'
								onClick={() => onClose()}
								data-mdb-ripple-init
								style={{
									borderRadius: '0px',
									height: '100%',
									width: '100%',
									fontSize: '18px',
									paddingTop: '10px',
									paddingBottom: '10px',
									boxShadow: 'none',
									backgroundColor: '#e6e6e6',
									color: '#3d4349',
									transition: 'background-color 0.3s ease',
								}}>
								{t('Cancel')}
							</button>
						</div>

						<div className='col d-flex align-items-center justify-content-center p-0'>
							<button
								type='button'
								className='btn btn'
								onClick={() => showCroppedImage()}
								data-mdb-ripple-init
								style={{
									borderRadius: '0px',
									height: '100%',
									width: '100%',
									fontSize: '18px',
									paddingTop: '10px',
									paddingBottom: '10px',
									boxShadow: 'none',
									color: 'white',
									backgroundColor: '#3aba6f',
									transition: 'background-color 0.3s ease',
								}}>
								{t('Done')}
							</button>
						</div>
					</div>
				</ModalContent>
			</Fade>
		</Modal>
	);
};

const Backdrop = React.forwardRef((props, ref) => {
	const { open, ...other } = props;
	return (
		<Fade in={open}>
			<div ref={ref} {...other} />
		</Fade>
	);
});

Backdrop.propTypes = {
	open: PropTypes.bool,
};

const blue = {
	200: '#99CCFF',
	300: '#66B2FF',
	400: '#3399FF',
	500: '#007FFF',
	600: '#0072E5',
	700: '#0066CC',
};

const grey = {
	50: '#F3F6F9',
	100: '#E5EAF2',
	200: '#DAE2ED',
	300: '#C7D0DD',
	400: '#B0B8C4',
	500: '#9DA8B7',
	600: '#6B7A90',
	700: '#434D5B',
	800: '#303740',
	900: '#1C2025',
};

const Modal = styled(BaseModal)`
	position: fixed;
	z-index: 1300;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
	z-index: -1;
	position: fixed;
	inset: 0;
	background-color: rgb(0 0 0 / 0.57);
	-webkit-tap-highlight-color: transparent;
`;
//rgb(181 171 171 / 0.3)
const ModalContent = styled('div')(
	({ theme }) => css`
		margin: 0 auto;
		width: 95%; /* default */
		text-align: center;
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 8px;
		overflow: hidden;
		background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
		border-radius: 20px;
		border: 0px solid transparent;
		outline: none;
		box-shadow: 0 4px 12px
			${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
		padding: 0px;
		color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};

		/* If screen < 649px → 90% width */
		@media (max-width: 649px) {
			width: 90%;
		}

		${theme.breakpoints.up('sm')} {
			/* ≥600px */
			width: 85%;
		}
		${theme.breakpoints.up('md')} {
			/* ≥900px */
			width: 70%;
		}
		${theme.breakpoints.up('lg')} {
			/* ≥1200px */
			width: 55%;
		}
		${theme.breakpoints.up('xl')} {
			/* ≥1536px */
			width: 40%;
		}
		@media (min-width: 1800px) {
			width: 35%;
		}

		& .modal-title {
			color: white;
			margin: 0;
			line-height: 1.5rem;
			margin-bottom: 8px;
		}

		& .modal-description {
			margin: 0;
			line-height: 1.5rem;
			font-weight: 400;
			color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
			margin-bottom: 4px;
		}
	`,
);
